import { mountToAppShell } from '../appShell'
import { NetworkManager } from '../../network/NetworkManager'
import { buildLeaderboardRow } from '../components/leaderboardRows'
import { msUntilNextUtcMidnight, formatCountdown } from '../components/CountdownUtcMidnight'

export interface LeaderboardEntry {
  rank: number
  username: string
  score: number
}

export interface TodayLeaderboardEntry {
  rank: number
  playerHash: string
  displayName: string
  score: number
  games: number
}

interface TodayMeData {
  rank: number
  score: number
  games: number
  scoreReachedAt?: string | null
}

interface TodayYesterdayBestData {
  starsWon: number | null
}

type Period = 'today' | 'daily' | 'weekly' | 'alltime'

const TODAY_POLL_BASE_MS = 30_000
const TODAY_POLL_JITTER_MS = 6_000

export class LeaderboardScreen {
  private container: HTMLDivElement
  private onClose: (() => void) | null = null
  private activePeriod: Period = 'today'

  // Today-specific state
  private todayEntries: TodayLeaderboardEntry[] = []
  private todayMe: TodayMeData | null = null
  private todayYesterdayBest: TodayYesterdayBestData | null = null
  private todayLoadError: string | null = null
  private todayLoading = false

  private countdownTimer: number | null = null
  private pollTimer: number | null = null
  private visibilityHandler: (() => void) | null = null

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'leaderboard-screen'
    this.container.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      display: flex; flex-direction: column; align-items: center;
      background: rgba(13,13,26,0.95); z-index: 210;
      font-family: 'Inter', sans-serif; color: #fff;
      overflow-y: auto; padding: 20px 0;
    `
  }

  show(onClose: () => void): void {
    this.onClose = onClose
    this.activePeriod = 'today'
    this.render([])
    mountToAppShell(this.container)
    this.installVisibilityHandler()
    void this.fetchToday()
  }

  hide(): void {
    this.clearCountdown()
    this.clearPollTimer()
    this.removeVisibilityHandler()
    this.container.remove()
  }

  dispose(): void {
    this.hide()
    this.onClose = null
  }

  // ===== Rendering =====

  private render(entries: LeaderboardEntry[]): void {
    // Clear container
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild)
    }

    const wrap = document.createElement('div')
    wrap.style.cssText = 'max-width: 400px; width: 90%;'

    wrap.appendChild(this.buildHeader())
    wrap.appendChild(this.buildTabs())

    const body = document.createElement('div')
    body.id = 'lb-body'
    body.style.minHeight = '200px'
    wrap.appendChild(body)

    this.container.appendChild(wrap)

    if (this.activePeriod === 'today') {
      this.renderTodayBody(body)
    } else {
      this.renderClassicBody(body, entries)
    }
  }

  private buildHeader(): HTMLElement {
    const header = document.createElement('div')
    header.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem;'

    const title = document.createElement('h2')
    title.style.cssText = 'font-size: 1.4rem; margin: 0;'
    title.textContent = this.activePeriod === 'today' ? 'Сегодня' : 'Leaderboard'
    header.appendChild(title)

    const closeBtn = document.createElement('button')
    closeBtn.style.cssText = `
      background: transparent; border: none; color: #aaa;
      font-size: 1.5rem; cursor: pointer; padding: 4px 8px;
    `
    closeBtn.textContent = 'X'
    closeBtn.addEventListener('click', () => {
      this.onClose?.()
      this.hide()
    })
    header.appendChild(closeBtn)

    return header
  }

  private buildTabs(): HTMLElement {
    const row = document.createElement('div')
    row.style.cssText = 'display: flex; gap: 8px; margin-bottom: 1.5rem; flex-wrap: wrap;'

    const tabs: Array<{ period: Period; label: string }> = [
      { period: 'today', label: 'Сегодня' },
      { period: 'daily', label: 'Daily' },
      { period: 'weekly', label: 'Weekly' },
      { period: 'alltime', label: 'All Time' },
    ]

    for (const { period, label } of tabs) {
      const btn = document.createElement('button')
      const isActive = this.activePeriod === period
      btn.style.cssText = `
        flex: 1; min-width: 70px; padding: 10px; font-size: 0.85rem; font-weight: 600;
        background: ${isActive ? '#FBBF24' : 'transparent'};
        border: 1px solid ${isActive ? '#FBBF24' : 'rgba(255,255,255,0.15)'};
        border-radius: 8px; color: ${isActive ? '#0f172a' : '#fff'}; cursor: pointer;
        transition: background 0.15s;
      `
      btn.textContent = label
      btn.addEventListener('click', () => {
        if (this.activePeriod === period) return
        this.switchTab(period)
      })
      row.appendChild(btn)
    }

    return row
  }

  private renderClassicBody(body: HTMLElement, entries: LeaderboardEntry[]): void {
    while (body.firstChild) body.removeChild(body.firstChild)

    if (entries.length === 0) {
      const loading = document.createElement('div')
      loading.style.cssText = 'text-align:center;color:#666;padding:40px;'
      loading.textContent = 'Loading...'
      body.appendChild(loading)
      return
    }

    for (const entry of entries.slice(0, 100)) {
      body.appendChild(this.buildRowSafe(entry.rank, entry.username, entry.score))
    }
  }

  private renderTodayBody(body: HTMLElement): void {
    while (body.firstChild) body.removeChild(body.firstChild)

    // Subtitle: yesterday's best
    if (this.todayYesterdayBest?.starsWon != null) {
      const subtitle = document.createElement('div')
      subtitle.style.cssText = `
        text-align: center; color: #FFD700; font-size: 0.95rem; font-weight: 600;
        margin-bottom: 0.75rem;
      `
      subtitle.textContent = `Вчера ты вынес ${this.todayYesterdayBest.starsWon}⭐`
      body.appendChild(subtitle)
    }

    // Countdown
    const countdownEl = document.createElement('div')
    countdownEl.id = 'lb-today-countdown'
    countdownEl.style.cssText = `
      text-align: center; color: #94a3b8; font-size: 0.8rem;
      margin-bottom: 1rem; letter-spacing: 0.05em;
    `
    countdownEl.textContent = `Сброс через ${formatCountdown(msUntilNextUtcMidnight(new Date()))}`
    body.appendChild(countdownEl)
    this.startCountdown()

    // Error or loading
    if (this.todayLoadError && this.todayEntries.length === 0) {
      const err = document.createElement('div')
      err.style.cssText = 'text-align:center;color:#E74C3C;padding:40px;'
      err.textContent = this.todayLoadError
      body.appendChild(err)
      return
    }

    if (this.todayLoading && this.todayEntries.length === 0) {
      const loading = document.createElement('div')
      loading.style.cssText = 'text-align:center;color:#666;padding:40px;'
      loading.textContent = 'Loading...'
      body.appendChild(loading)
      return
    }

    if (this.todayEntries.length === 0) {
      const empty = document.createElement('div')
      empty.style.cssText = 'text-align:center;color:#666;padding:40px;'
      empty.textContent = 'Пока никто не играл сегодня'
      body.appendChild(empty)
    } else {
      const list = document.createElement('div')
      list.style.cssText = 'display: flex; flex-direction: column; gap: 4px;'
      for (const entry of this.todayEntries.slice(0, 50)) {
        list.appendChild(this.buildTodayRow(entry))
      }
      body.appendChild(list)
    }

    // Sticky "me" row if rank > 50
    if (this.todayMe && this.todayMe.rank > 50) {
      const sticky = document.createElement('div')
      sticky.style.cssText = `
        position: sticky; bottom: 0; margin-top: 12px;
        background: rgba(13,13,26,0.98);
        border-top: 1px solid rgba(251,191,36,0.4);
        padding-top: 8px;
      `
      const meName = NetworkManager.getInstance().defaultDisplayName || 'You'
      sticky.appendChild(this.buildTodayMeRow(this.todayMe, meName))
      body.appendChild(sticky)
    }
  }

  private buildRowSafe(rank: number, name: string, score: number): HTMLElement {
    // Classic leaderboards (daily/weekly/alltime) use bare score, no games count.
    const row = buildLeaderboardRow({ rank, displayName: name, score })
    row.style.marginBottom = '4px'
    return row
  }

  private buildTodayRow(entry: TodayLeaderboardEntry): HTMLElement {
    return buildLeaderboardRow(
      { rank: entry.rank, displayName: entry.displayName, score: entry.score, games: entry.games },
      { showGames: true }
    )
  }

  private buildTodayMeRow(me: TodayMeData, displayName: string): HTMLElement {
    return buildLeaderboardRow(
      { rank: me.rank, displayName, score: me.score, games: me.games },
      { isMe: true, showGames: true }
    )
  }

  // ===== Tab switching =====

  private switchTab(period: Period): void {
    this.activePeriod = period
    this.clearPollTimer()
    this.clearCountdown()

    if (period === 'today') {
      this.render([])
      void this.fetchToday()
    } else {
      this.render([])
      void this.fetchLeaderboard(period)
    }
  }

  // ===== Classic fetching =====

  private async fetchLeaderboard(period: Exclude<Period, 'today'>): Promise<void> {
    try {
      const res = await fetch(`/api/leaderboard?period=${period}`)
      const data = await res.json()

      if (this.activePeriod !== period) return // user switched away

      if (data.success) {
        this.render(data.data.entries as LeaderboardEntry[])
      } else {
        this.showClassicError()
      }
    } catch {
      if (this.activePeriod !== period) return
      this.showClassicError()
    }
  }

  private showClassicError(): void {
    const body = this.container.querySelector('#lb-body')
    if (!body) return
    while (body.firstChild) body.removeChild(body.firstChild)
    const err = document.createElement('div')
    err.style.cssText = 'text-align:center;color:#E74C3C;padding:40px;'
    err.textContent = 'Failed to load'
    body.appendChild(err)
  }

  // ===== Today fetching =====

  private async fetchToday(): Promise<void> {
    if (this.activePeriod !== 'today') return

    this.todayLoading = true
    this.todayLoadError = null

    const token = NetworkManager.getInstance().getToken()
    const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

    try {
      const [topRes, meRes, yesterdayRes] = await Promise.allSettled([
        fetch('/api/daily-leaderboard/top?limit=50'),
        token
          ? fetch('/api/daily-leaderboard/me', { headers: authHeaders })
          : Promise.resolve(null as unknown as Response),
        token
          ? fetch('/api/daily-leaderboard/yesterday-best', { headers: authHeaders })
          : Promise.resolve(null as unknown as Response),
      ])

      if (this.activePeriod !== 'today') return

      // Top
      if (topRes.status === 'fulfilled' && topRes.value) {
        try {
          const data = await topRes.value.json()
          if (data?.success && Array.isArray(data?.data?.entries)) {
            this.todayEntries = (data.data.entries as TodayLeaderboardEntry[]).map((e) => ({
              rank: Number(e.rank) || 0,
              playerHash: String(e.playerHash ?? ''),
              displayName: String(e.displayName ?? ''),
              score: Number(e.score) || 0,
              games: Number(e.games) || 0,
            }))
          }
        } catch {
          // swallow per-endpoint parse errors
        }
      }

      // Me
      if (meRes.status === 'fulfilled' && meRes.value) {
        try {
          const data = await meRes.value.json()
          if (data?.success) {
            if (data.data && typeof data.data === 'object') {
              this.todayMe = {
                rank: Number(data.data.rank) || 0,
                score: Number(data.data.score) || 0,
                games: Number(data.data.games) || 0,
                scoreReachedAt: data.data.scoreReachedAt ?? null,
              }
            } else {
              this.todayMe = null
            }
          }
        } catch {
          // swallow
        }
      }

      // Yesterday best
      if (yesterdayRes.status === 'fulfilled' && yesterdayRes.value) {
        try {
          const data = await yesterdayRes.value.json()
          if (data?.success && data?.data) {
            const stars = data.data.starsWon
            this.todayYesterdayBest = {
              starsWon: typeof stars === 'number' ? stars : null,
            }
          }
        } catch {
          // swallow
        }
      }

      if (this.todayEntries.length === 0 && this.todayMe === null && this.todayYesterdayBest === null) {
        // Best-effort: if all three failed catastrophically (none fulfilled with content) show error
        const allRejected = topRes.status !== 'fulfilled'
          && meRes.status !== 'fulfilled'
          && yesterdayRes.status !== 'fulfilled'
        if (allRejected) {
          this.todayLoadError = 'Не удалось загрузить таблицу'
        }
      }
    } catch {
      this.todayLoadError = 'Не удалось загрузить таблицу'
    } finally {
      this.todayLoading = false
      if (this.activePeriod === 'today') {
        this.render([])
        this.schedulePoll()
      }
    }
  }

  // ===== Polling =====

  private schedulePoll(): void {
    this.clearPollTimer()
    if (this.activePeriod !== 'today') return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return

    const jitter = (Math.random() * 2 - 1) * TODAY_POLL_JITTER_MS
    const delay = TODAY_POLL_BASE_MS + jitter
    this.pollTimer = window.setTimeout(() => {
      this.pollTimer = null
      if (this.activePeriod !== 'today') return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      void this.fetchToday()
    }, delay)
  }

  private clearPollTimer(): void {
    if (this.pollTimer !== null) {
      window.clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  private installVisibilityHandler(): void {
    if (typeof document === 'undefined') return
    const handler = (): void => {
      if (document.visibilityState === 'visible') {
        if (this.activePeriod === 'today' && this.pollTimer === null) {
          this.schedulePoll()
        }
      } else {
        this.clearPollTimer()
      }
    }
    this.visibilityHandler = handler
    document.addEventListener('visibilitychange', handler)
  }

  private removeVisibilityHandler(): void {
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    this.visibilityHandler = null
  }

  // ===== Countdown =====

  private startCountdown(): void {
    this.clearCountdown()
    this.countdownTimer = window.setInterval(() => {
      const el = this.container.querySelector('#lb-today-countdown')
      if (!el) {
        this.clearCountdown()
        return
      }
      el.textContent = `Сброс через ${formatCountdown(msUntilNextUtcMidnight(new Date()))}`
    }, 1000)
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }
  }
}
